import { Dispatch, forwardRef, Ref, SetStateAction } from 'react';

import { usePermissions } from '@mint-lib/profile-helpers';
import styled, { isPropValid } from '@mint-lib/styled';

import { BUILDER_ROW_TYPES } from '../../../../constants/campaignBuilder.js';
import { useBuilderTableViewDataSelector } from '../../../../hooks/useGetBuilderTableViewData.js';
import { useTrackingInitiator } from '../../../../hooks/usePerformanceTracking.js';
import { usePredictivePlanningMode } from '../../../../hooks/usePredictivePlanningMode.js';
import usePreparedBuilderTableColumns from '../../../../hooks/usePreparedBuilderTableColumns.js';
import { useSaveBuilderTableColumnOrder } from '../../../../hooks/useSaveBuilderTableView.js';
import { useTableScale } from '../../../../hooks/useTableScale.js';
import { TRACKING_EVENTS } from '../../../../types/performanceTracking.js';
import { Header } from '../../../../types/shared.js';
import { changeColumnsOrder } from '../../../../utils/campaigns/myCampaigns.js';
import TableHeadItem from './TableHeadItem/TableHeadItem.jsx';

type Props = {
  headerGroups: Array<any>;
  setColumnOrder: (columns: Array<string>) => void;
  isPanelOpened?: boolean;
  onSetColumnRef: any;
  isResizingColumn?: string;
  expandedType?: BUILDER_ROW_TYPES;
  setExpandedType?: Dispatch<SetStateAction<BUILDER_ROW_TYPES>>;
  columnOrder: any;
  columnsFromSchema: any;
};

const BuilderTableHead = forwardRef(
  (
    {
      headerGroups,
      setColumnOrder,
      onSetColumnRef,
      isResizingColumn,
      expandedType,
      setExpandedType,
    }: Props,

    headRef: Ref<HTMLTableRowElement>,
  ) => {
    const {
      permissions: { campaignPlanningFeature },
    } = usePermissions();

    const scale = useTableScale();
    const { columnNames, initialColumns } = usePreparedBuilderTableColumns();
    const { data: builderTableViewId } = useBuilderTableViewDataSelector('id');
    const saveBuilderTableColumnOrder = useSaveBuilderTableColumnOrder();

    const startMeasurePerformance = useTrackingInitiator();
    const isPredictivePlanningMode = usePredictivePlanningMode();

    const handleChangeOrder = (dragHeaderId: string, dropHeaderId: string) => {
      if (builderTableViewId) {
        const { headers, columns } = changeColumnsOrder({
          dragHeaderId,
          dropHeaderId,
          columnOrder: columnNames,
          columnsFromSchema: initialColumns,
        });

        saveBuilderTableColumnOrder({ id: builderTableViewId, columns });
        setColumnOrder(headers);
        startMeasurePerformance(TRACKING_EVENTS.COLUMN_REORDER);
      }
    };
    return (
      <Thead>
        {headerGroups.map((headerGroup) => (
          <Tr
            {...headerGroup.getHeaderGroupProps()}
            ref={headRef}
            $zIndex={headerGroup.headers.length}
            className="tr"
            $scale={scale}
          >
            {headerGroup.headers.map((header: Header, idx: number) => (
              <TableHeadItem
                header={header}
                onChangeOrder={handleChangeOrder}
                key={header.id}
                setRef={onSetColumnRef}
                headerIdx={headerGroup.headers.length - idx}
                isDisabled={!campaignPlanningFeature}
                isResizingColumn={isResizingColumn}
                expandedType={expandedType}
                setExpandedType={setExpandedType}
                isPredictivePlanningMode={isPredictivePlanningMode}
                scale={scale}
              />
            ))}
          </Tr>
        ))}
      </Thead>
    );
  },
);

const Thead = styled('div')`
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 2;
`;

const Tr = styled('div', { shouldForwardProp: isPropValid })<{
  $zIndex: number;
  $scale: number;
}>`
  display: flex;
  align-items: flex-start;
  height: ${({ $scale }) => $scale * 40}px;
  & > .th {
    position: sticky;
    top: 0;

    &:first-of-type {
      left: 0;
    }
  }
`;

export default BuilderTableHead;
